import { ApiError } from "@/core/api/types";

export const PASSWORD_RESET_ERROR_CODE = {
  invalidCode: "invalid_code",
  invalidPassword: "invalid_password",
  loginFailed: "login_failed",
  throttled: "throttled",
  unknown: "unknown",
} as const;

export type PasswordResetErrorCode =
  (typeof PASSWORD_RESET_ERROR_CODE)[keyof typeof PASSWORD_RESET_ERROR_CODE];

export class PasswordResetError extends Error {
  constructor(
    public readonly code: PasswordResetErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "PasswordResetError";
  }
}

export const isInvalidPasswordResetCodeError = (
  error: unknown,
): error is PasswordResetError =>
  error instanceof PasswordResetError &&
  error.code === PASSWORD_RESET_ERROR_CODE.invalidCode;

export const isPasswordResetThrottledError = (
  error: unknown,
): error is PasswordResetError =>
  error instanceof PasswordResetError &&
  error.code === PASSWORD_RESET_ERROR_CODE.throttled;

export const isThrottledApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.status === 429;

export const RESEND_CODE_COOLDOWN_SECONDS = 30;

export const describeWait = (seconds?: number) => {
  if (seconds === undefined) return "later";
  const plural = (count: number, unit: string) =>
    `in ${count} ${unit}${count === 1 ? "" : "s"}`;

  if (seconds < 60) return plural(Math.max(1, seconds), "second");
  if (seconds < 3600) return plural(Math.ceil(seconds / 60), "minute");
  return plural(Math.ceil(seconds / 3600), "hour");
};

export const getPasswordResetRequestError = (
  error: unknown,
  waitSeconds?: number,
) => {
  if (!isThrottledApiError(error)) return error;

  return new PasswordResetError(
    PASSWORD_RESET_ERROR_CODE.throttled,
    `Too many code requests. Use the latest code in your email, or try again ${describeWait(waitSeconds ?? error.retryAfterSeconds)}.`,
  );
};

const getCodeCheckThrottledError = (error: ApiError) =>
  new PasswordResetError(
    PASSWORD_RESET_ERROR_CODE.throttled,
    `Too many attempts. Try again ${describeWait(error.retryAfterSeconds)}.`,
  );

export const getPasswordResetVerificationError = (error: unknown) => {
  if (!(error instanceof ApiError)) return error;
  if (error.status === 429) return getCodeCheckThrottledError(error);

  if (error.status === 422) {
    return new PasswordResetError(
      PASSWORD_RESET_ERROR_CODE.invalidCode,
      "Invalid verification code",
    );
  }

  return new PasswordResetError(
    PASSWORD_RESET_ERROR_CODE.unknown,
    "Failed to verify forgot password code",
  );
};

export const getPasswordResetSubmissionError = (error: unknown) => {
  if (!(error instanceof ApiError)) return error;
  if (error.status === 429) return getCodeCheckThrottledError(error);

  const { status } = error;
  const detail = error.message;

  // A 422 covers two different cases: an invalid/expired verification code,
  // or a validation failure while saving the user. Devise `:validatable`
  // validates the whole record on update, so this can surface unrelated errors
  // such as a bad phone number.
  if (status === 422) {
    if (detail.toLowerCase().includes("verification code")) {
      return new PasswordResetError(
        PASSWORD_RESET_ERROR_CODE.invalidCode,
        "Invalid or expired reset link",
      );
    }

    return new PasswordResetError(
      PASSWORD_RESET_ERROR_CODE.invalidPassword,
      detail || "Password could not be set",
    );
  }

  return new PasswordResetError(
    PASSWORD_RESET_ERROR_CODE.unknown,
    detail || `Request failed (status ${status})`,
  );
};
